// UI-6: the bundled sample project — a small "finished" drawing novices can inspect:
// traced room outline, a wall, a door arc, associative dimensions, a label, and a grid.
import { createEmptyDoc, defaultActiveLayerId, defaultStyle, defaultTextStyle, newId } from "../doc/document";
import type { Doc, Entity } from "../doc/entities";
import { applyCommand } from "../doc/commands";

export function buildSampleDoc(): Doc {
  const doc = createEmptyDoc("Sample plan");
  const drawing = defaultActiveLayerId(doc);
  const annotations = doc.layers[2]!.id;

  const outline: Entity = {
    id: "sample-outline",
    type: "polyline",
    layerId: drawing,
    locked: false,
    hidden: false,
    closed: true,
    points: [
      { x: 0, y: 0 },
      { x: 4200, y: 0 },
      { x: 4200, y: 3000 },
      { x: 2400, y: 3000 },
      { x: 2400, y: 3600 },
      { x: 0, y: 3600 }
    ],
    style: { ...defaultStyle(), strokeWidth: 0.5 }
  };
  const innerWall: Entity = {
    id: "sample-wall",
    type: "polyline",
    layerId: drawing,
    locked: false,
    hidden: false,
    closed: false,
    points: [
      { x: 2400, y: 0 },
      { x: 2400, y: 2100 }
    ],
    style: defaultStyle()
  };
  const door: Entity = {
    id: "sample-door",
    type: "arc",
    layerId: drawing,
    locked: false,
    hidden: false,
    center: { x: 2400, y: 2100 },
    r: 820,
    startDeg: 90,
    sweepDeg: 90,
    style: { ...defaultStyle(), dash: "dashed" }
  };
  const dims: Entity[] = [
    {
      id: "sample-dim-w",
      type: "dimension",
      layerId: annotations,
      locked: false,
      hidden: false,
      a: { kind: "entity", entityId: "sample-outline", anchor: "vertex", index: 0 },
      b: { kind: "entity", entityId: "sample-outline", anchor: "vertex", index: 1 },
      offset: -350,
      orient: "h",
      kind: "linear"
    },
    {
      id: "sample-dim-h",
      type: "dimension",
      layerId: annotations,
      locked: false,
      hidden: false,
      a: { kind: "entity", entityId: "sample-outline", anchor: "vertex", index: 1 },
      b: { kind: "entity", entityId: "sample-outline", anchor: "vertex", index: 2 },
      offset: -350,
      orient: "v",
      kind: "linear"
    }
  ];
  const label: Entity = {
    id: "sample-label",
    type: "label",
    layerId: annotations,
    locked: false,
    hidden: false,
    head: { x: 2400, y: 1000 },
    tail: { x: 3200, y: 700 },
    content: "move me — the dimension follows",
    textStyle: { ...defaultTextStyle(), size: 120 },
    arrow: "arrow",
    style: defaultStyle()
  };
  const title: Entity = {
    id: "sample-title",
    type: "text",
    layerId: annotations,
    locked: false,
    hidden: false,
    pos: { x: 0, y: 3900 },
    rotationDeg: 0,
    content: "SAMPLE PLAN — try: drag a wall with E held · press D and click two corners · Ctrl+E to print",
    textStyle: { ...defaultTextStyle(), size: 140 }
  };
  const grid: Entity = {
    id: "sample-grid",
    type: "grid",
    layerId: doc.layers[0]!.id,
    locked: false,
    hidden: false,
    pos: { x: 5000, y: 0 },
    rotationDeg: 0,
    cols: 4,
    rows: 3,
    cellW: 500,
    cellH: 500,
    lockAspect: true,
    labels: {
      cols: true,
      rows: true,
      colMode: "letters",
      rowMode: "numbers",
      textStyle: { ...defaultTextStyle(), size: 150, color: "#8a90a0" }
    },
    vStyle: { color: "#4dd0e1", width: 2, dash: "solid" },
    hStyle: { color: "#4dd0e1", width: 2, dash: "solid" },
    majorEvery: 0
  };

  const entities = [outline, innerWall, door, ...dims, label, title, grid];
  applyCommand(doc, {
    kind: "addEntities",
    entities: entities.map((e) => ({ ...e, id: e.id || newId() })),
    z: entities.map((e) => ({ layerId: e.layerId, index: -1 }))
  });
  return doc;
}
